import React, { useState } from "react";
import Breadcrumb from "../../common/BreadCrumb";
import { useForm } from "react-hook-form";
import { doObjToFormData, getFileIcon } from "../../../helpers/helpers";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { useSelector } from "react-redux";
import http from "../../../helpers/http";
import { getAuthUserToken } from "../../../helpers/authToken";
import { toast, ToastContainer } from "react-toastify";
import { TOAST_SETTINGS } from "../../../utils/siteSettings";
import { Form, useNavigate } from "react-router-dom";
import Text from "../../common/Text";
import FormProcessingSpinner from "../../common/FormProcessingSpinner";

const CVGeneratorForm = ({ showCv, toggleCV, setUserCvData, siteSettings }) => {
  const [isGeneratingCV, setIsGeneratingCV] = useState(false);
  const breadcrumbItems = [
    { name: "Dashboard", link: "/dashboard" },
    { name: "CV Generator", link: "/cv-generator" },
  ];
  const [file, setFile] = useState(null);
  const [fileShow, setFileShow] = useState(null);
  const removeFile = () => {
    setFile(null);
    setFileShow(null);
    document.getElementById("cvFile").value = null;
  };
  const [error, setError] = useState("");
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();
  const handleFileChange = (e) => {
    const uploaded = e.target.files[0];
    if (!uploaded) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(uploaded.type)) {
      setError("Only PDF, DOC, DOCX files are allowed.");
      setFile(null);
      return;
    }

    if (uploaded.size > maxSize) {
      setError("File size should not exceed 5MB.");
      setFile(null);
      return;
    }

    setFile(e.target.files[0] ? e.target.files : null);
    setFileShow(uploaded);
    setError("");
  };
  const handelGenerateCV = (frmData) => {
    // ✅ make sure file is a File object, not event.target
    // if (file !== null) {
    //     frmData.cv = file;
    // }

    // ✅ convert to FormData
    const fd = new FormData();
    Object.keys(frmData).forEach((key) => {
      if (frmData[key] instanceof File) {
        fd.append(key, frmData[key]); // file goes here as "cv"
      } else {
        fd.append(key, frmData[key]);
      }
    });

    // ✅ add token separately
    fd.append("token", getAuthUserToken());
    fd.append("cv", file ? file[0] : null);

    // Debug check
    for (let pair of fd.entries()) {
      // console.log(pair[0] + ":", pair[1]);
    }
    setIsGeneratingCV(true);
    http
      .post("user/generate-cv-with-ai", fd)
      .then(({ data }) => {
        setIsGeneratingCV(false);
        if (data.status) {
          document.getElementById("cvFile").value = null;
          if (data?.generated_cv?.id > 0) {
            setUserCvData(data.generated_cv);
            toast.success("CV Generated Successfully", TOAST_SETTINGS);
            toggleCV();
          } else {
            toast.error(
              "Failed to generate CV. Please try again.",
              TOAST_SETTINGS
            );
          }
        } else {
          if (data.validationErrors) {
            toast.error(
              <Text string={data.validationErrors} parse={true} />,
              TOAST_SETTINGS
            );
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <section id="dash">
        <ToastContainer />
        <Breadcrumb items={breadcrumbItems} />
        <div className="contain">
          <div className="dash_new_txt text-center">
            {/* <h3>🎯 Smart CV Generator</h3>
            <p>Create tailored CVs that match job requirements perfectly</p> */}
            <h3>{siteSettings?.site_cv_page_heading}</h3>
            <p>{siteSettings?.site_cv_page_text}</p>
            <a
              href={siteSettings?.site_cv_page_link}
              target="_blank"
              className="color"
              rel="noopener noreferrer"
              style={{
                textDecoration: "underline",
                fontWeight: "500",
              }}
            >
              {siteSettings?.site_cv_page_link_text}
            </a>
          </div>
          <div className="outer_cmn_blk_dash cv_generator_blk">
            <form
              className="cmn_blk_new_dash"
              method="POST"
              onSubmit={handleSubmit(handelGenerateCV)}
            >
              <div className="flex">
                <div className="col">
                  <div className="top_head">
                    <div className="image_icon">
                      <img src="/images/cv-generator/icon1.svg" alt="" />
                    </div>
                    <div className="r_txt">
                      <h4 className="heading_dash">Job Details</h4>
                      <p>Enter job title and paste the complete job posting</p>
                    </div>
                  </div>
                  <div className="form_row row">
                    <div className="col-sm-12">
                      <h5>Job Title</h5>
                      <div className="form_blk">
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g. Senior Software Engineer, Marketing Manager..."
                          {...register("job_title", {
                            required: "Job Title is required.",
                          })}
                        />
                        {errors.job_title && (
                          <span className="validation-error">
                            Title is required
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-12">
                      <h5>Job Description</h5>
                      <div className="form_blk">
                        <textarea
                          name=""
                          id=""
                          placeholder="Paste the complete job description here including requirements, responsibilities, and qualifications..."
                          className="input"
                          {...register("job_description", {
                            required: "Job Description is required.",
                          })}
                        ></textarea>
                        {errors.job_description && (
                          <span className="validation-error">
                            Job Description is required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="top_head">
                    <div className="image_icon">
                      <img src="/images/cv-generator/icon2.svg" alt="" />
                    </div>
                    <div className="r_txt">
                      <h4 className="heading_dash">Your Current CV</h4>
                      <p>Upload your latest resume</p>
                    </div>
                  </div>
                  <div className="form_row row">
                    <div className="col-sm-12">
                      <div className="upload_resume">
                        <div className="upload_icon">
                          <img src="/images/cv-generator/upload.svg" />
                        </div>
                        <h6>Drop your CV here</h6>
                        <p>
                          <small>or click to browse files</small>
                        </p>
                        <div className="btn_blk">
                          <label htmlFor="cvFile" className="site_btn">
                            Choose File
                          </label>
                          <input
                            type="file"
                            id="cvFile"
                            accept=".pdf,.doc,.docx"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                          />
                        </div>
                        <p>Supports PDF, DOC, DOCX files up to 5MB</p>
                        <div className="upload_show_file">
                          {error && (
                            <span className="validation-error">{error}</span>
                          )}
                          {file && (
                            <div className="inner_show_file">
                              <div className="up_file_sh">
                                <img
                                  src={getFileIcon(
                                    fileShow.name.split(".").pop().toLowerCase()
                                  )}
                                  alt="file icon"
                                  style={{ width: "6%" }}
                                />
                                <span>{fileShow.name}</span>
                              </div>
                              <div className="x_btn" onClick={removeFile}></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="btn_blk text-center edit_blk">
                <button
                  type="submit"
                  className="site_btn"
                  disabled={isGeneratingCV}
                  // onClick={toggleCV}
                >
                  <img src="/images/cv-generator/edit.svg" />
                  <span>Generate Tailored CV</span>
                  <FormProcessingSpinner isFormProcessing={isGeneratingCV} />
                </button>
              </div>

              <div className="tips_blk">
                <div className="icon_tip">
                  <img src="/images/cv-generator/bulb.svg" alt="" />
                </div>
                <div className="txt_right">
                  <h5>How it works</h5>
                  <ul>
                    <li>
                      AI analyzes the job requirements and your CV content
                    </li>
                    <li>Optimizes keywords and skills to match the position</li>
                    <li>Restructures sections for maximum impact</li>
                    <li>
                      Generates a professionally formatted, ATS-friendly CV
                    </li>
                  </ul>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default CVGeneratorForm;
